import { useMemo, useRef, useState, type ReactNode } from "react";
import { IconChecks, IconChevronDown, IconChevronUp, IconSearch, IconSelector } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import {
  Badge,
  Card,
  Checkbox,
  DatePickerInput,
  DesktopOnly,
  Group,
  Input,
  Loader,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  TruncatedTooltip,
  Tr,
} from "@/components/ui";
import type { BandejaAutorizacionItem } from "@/lib/types";
import { formatearMonto } from "@/shared/format/money";
import { parseIso, formatIso } from "@/features/datos-generales/utils/iso-date";
import { etiquetaTipoSolicitud, etiquetaSubtipoSolicitud } from "@/lib/solicitud-labels";
import { formatFechaHora } from "@/shared/format/fecha";
import {
  filtrarBandeja,
  ordenarBandeja,
  siguienteSort,
  type BandejaFiltros,
  type BandejaSort,
  type BandejaSortKey,
} from "../utils/ordenar-bandeja";

/**
 * Columnas de datos (sin la de selección, que va aparte al inicio) · el orden y
 * la cantidad deben coincidir EXACTAMENTE con las celdas de `BandejaDesktopRow`.
 * `key` presente → columna ordenable + filtrable; `key: null` → columna sin
 * orden/filtro (Acciones). Las columnas de fecha filtran por DÍA con un selector.
 */
export const COLUMNAS_BANDEJA: {
  key: BandejaSortKey | null;
  label: string;
  align?: "right" | "center";
  w: number;
  filtro?: "fecha";
}[] = [
  { key: "folio", label: "Folio", w: 90 },
  { key: "organizacion", label: "Organización", w: 150 },
  { key: "area", label: "Área", w: 120 },
  { key: "tipo", label: "Tipo / Subtipo", w: 150 },
  { key: "proveedor", label: "Proveedor", w: 190 },
  { key: "numProveedor", label: "Núm. Prov", w: 90 },
  { key: "rfcEmisor", label: "RFC Emisor", w: 140 },
  { key: "descripcion", label: "Descripción", w: 190 },
  { key: "total", label: "Total", align: "right", w: 120 },
  { key: "moneda", label: "Moneda", w: 80 },
  { key: "nivel", label: "Nivel", w: 90 },
  { key: "fechaCreacion", label: "Creación", w: 150, filtro: "fecha" },
  { key: null, label: "Acciones", align: "center", w: 110 },
];

const COLS = COLUMNAS_BANDEJA;

// Ancho de la columna de selección (checkbox).
const W_CHECK = 44;

// Suma exacta de los anchos · con `layout: fixed` la tabla mide esto y cada
// columna respeta su ancho (los textos largos TRUNCAN + tooltip).
export const MIN_WIDTH_BANDEJA = W_CHECK + COLS.reduce((s, c) => s + c.w, 0);

/**
 * Vista de escritorio de la bandeja · MISMO estilo que las tablas de Mis
 * solicitudes / Mis facturas: contenedor `Card`, encabezado con fondo gris
 * (`ink-100`) sticky, franja de filtros tenue (`foreground/10`) COLAPSABLE
 * (toggle en la toolbar de la página), espaciado `lg` y barra de scroll
 * horizontal ESPEJO arriba de la tabla. Conserva la columna de selección y la
 * acción "Resolver" para la autorización (individual y masiva). Orden por
 * columna (click en el encabezado, cliente) y filtro de texto/fecha por columna
 * (server-side + cliente sobre la página visible para feedback instantáneo).
 * Mobile usa `BandejaMobileList`.
 */
export function BandejaDesktopTable({
  items,
  filtros,
  buscando = false,
  mostrarFiltros = true,
  onFiltrar,
  seleccionadas,
  onToggleUno,
  onToggleVisibles,
}: {
  items: BandejaAutorizacionItem[];
  filtros: BandejaFiltros;
  buscando?: boolean;
  /** Fila de filtros por columna colapsable · el toggle vive en la toolbar de la página. */
  mostrarFiltros?: boolean;
  onFiltrar: (key: BandejaSortKey, valor: string) => void;
  /** Selección para autorización masiva (por id). */
  seleccionadas: Set<string>;
  onToggleUno: (id: string) => void;
  onToggleVisibles: (ids: string[], marcar: boolean) => void;
}) {
  const [sort, setSort] = useState<BandejaSort | null>(null);

  const visibles = useMemo(
    () => ordenarBandeja(filtrarBandeja(items, filtros), sort),
    [items, filtros, sort],
  );

  const idsVisibles = visibles.map((v) => v.id);
  const todasVisiblesMarcadas = idsVisibles.length > 0 && idsVisibles.every((id) => seleccionadas.has(id));

  const alOrdenar = (key: BandejaSortKey) => setSort((prev) => siguienteSort(prev, key));

  // Scrollbar horizontal DUPLICADO arriba de la tabla (espejo · mismo patrón que
  // Mis facturas): una barra superior sincronizada en ambos sentidos con el
  // contenedor real de la tabla, para no tener que bajar a buscar la nativa.
  const tablaScrollRef = useRef<HTMLDivElement>(null);
  const barraSuperiorRef = useRef<HTMLDivElement>(null);

  const alScrollBarra = () => {
    if (tablaScrollRef.current && barraSuperiorRef.current)
      tablaScrollRef.current.scrollLeft = barraSuperiorRef.current.scrollLeft;
  };
  const alScrollTabla = () => {
    if (tablaScrollRef.current && barraSuperiorRef.current)
      barraSuperiorRef.current.scrollLeft = tablaScrollRef.current.scrollLeft;
  };

  return (
    <DesktopOnly>
      <Card padding="xs" className="relative p-0">
        {/* Indicador de búsqueda en curso · no bloquea (el usuario puede seguir escribiendo). */}
        {buscando && (
          <Group gap={6} className="pointer-events-none absolute right-3 top-2 z-20">
            <Loader size="xs" color="multiva" />
            <Text size="xs" c="dimmed" fw={500}>
              Buscando…
            </Text>
          </Group>
        )}

        {/* Barra de scroll horizontal ESPEJO arriba de la tabla · replica el ancho
            real de la tabla y se sincroniza en ambos sentidos con el contenedor. */}
        <div
          ref={barraSuperiorRef}
          onScroll={alScrollBarra}
          className="scrollbar-suave h-3 shrink-0 overflow-x-auto overflow-y-hidden"
          aria-hidden="true"
        >
          {/* Ancho por CSS EN VIVO (w-full + min-width) · sin lag de ResizeObserver
              que hacía aparecer un scrollbar transitorio al animar el sidebar. */}
          <div className="h-px w-full" style={{ minWidth: MIN_WIDTH_BANDEJA }} />
        </div>

        {/* Altura acotada · el scroll vertical ocurre DENTRO del contenedor para que
            el `thead` sticky se pegue. El scroll HORIZONTAL no muestra barra abajo
            (se conduce SOLO por la barra espejo de arriba). */}
        <div
          ref={tablaScrollRef}
          onScroll={alScrollTabla}
          className="scrollbar-suave max-h-[calc(100dvh-16rem)] overflow-x-hidden overflow-y-auto"
        >
          <Table
            layout="fixed"
            highlightOnHover
            verticalSpacing="md"
            horizontalSpacing="sm"
            minWidth={MIN_WIDTH_BANDEJA}
            className="uppercase"
          >
            {/* Encabezado STICKY con fondo gris opaco (`ink-100`). */}
            <Thead className="sticky top-0 z-10 bg-ink-100 dark:bg-ink-800">
              <Tr>
                <Th className="!px-2 align-middle" style={{ width: W_CHECK }}>
                  <Checkbox
                    aria-label="Seleccionar todas las visibles"
                    checked={todasVisiblesMarcadas}
                    onCheckedChange={(v) => onToggleVisibles(idsVisibles, Boolean(v))}
                  />
                </Th>
                {COLS.map((c, i) =>
                  c.key ? (
                    <ThOrdenable
                      key={c.key}
                      sortKey={c.key}
                      sort={sort}
                      onSort={alOrdenar}
                      align={c.align === "right" ? "right" : "left"}
                      w={c.w}
                    >
                      {c.label}
                    </ThOrdenable>
                  ) : (
                    <Th key={`x-${i}`} align={c.align} style={{ width: c.w, verticalAlign: "middle" }}>
                      <Text size="xs" fw={600} className="whitespace-nowrap leading-tight">
                        {c.label}
                      </Text>
                    </Th>
                  ),
                )}
              </Tr>
              {/* Franja de filtros tenue · igual que Mis facturas · colapsable. */}
              {mostrarFiltros && (
                <Tr className="border-t border-border bg-foreground/10">
                  <Th className="!px-1 !py-1.5" />
                  {COLS.map((c, i) => {
                    if (!c.key) return <Th key={`xf-${i}`} className="!px-1 !py-1.5" />;
                    const key = c.key;
                    return (
                      <Th key={key} className="!px-1 !py-1.5 align-top">
                        {c.filtro === "fecha" ? (
                          <DatePickerInput
                            size="xs"
                            clearable
                            placeholder="Fecha…"
                            value={parseIso(filtros[key])}
                            onChange={(d) => onFiltrar(key, formatIso(d))}
                            wrapperClassName="normal-case"
                            placeholderClassName="truncate text-muted-foreground/60"
                          />
                        ) : (
                          <Input
                            size="xs"
                            placeholder={`${c.label}…`}
                            leftSection={<IconSearch size={12} stroke={1.6} />}
                            value={filtros[key] ?? ""}
                            onChange={(e) => onFiltrar(key, e.currentTarget.value)}
                            aria-label={`Filtrar por ${c.label}`}
                            className="normal-case overflow-hidden text-ellipsis whitespace-nowrap placeholder:text-muted-foreground/60"
                          />
                        )}
                      </Th>
                    );
                  })}
                </Tr>
              )}
            </Thead>
            <Tbody className="transition-opacity duration-120" style={{ opacity: buscando ? 0.5 : 1 }}>
              {visibles.length === 0 ? (
                <Tr>
                  <Td colSpan={COLS.length + 1}>
                    <Text size="sm" c="dimmed" ta="center" className="py-3 normal-case">
                      Sin resultados para los filtros.
                    </Text>
                  </Td>
                </Tr>
              ) : (
                visibles.map((it) => (
                  <BandejaDesktopRow
                    key={it.id}
                    it={it}
                    seleccionada={seleccionadas.has(it.id)}
                    onToggle={() => onToggleUno(it.id)}
                  />
                ))
              )}
            </Tbody>
          </Table>
        </div>
      </Card>
    </DesktopOnly>
  );
}

/** Encabezado clickeable con indicador de orden (mismo patrón que Mis facturas). */
function ThOrdenable({
  children,
  sortKey,
  sort,
  onSort,
  align = "left",
  w,
}: {
  children: ReactNode;
  sortKey: BandejaSortKey;
  sort: BandejaSort | null;
  onSort: (key: BandejaSortKey) => void;
  align?: "left" | "right";
  w?: number;
}) {
  const activa = sort?.key === sortKey;
  const Icono = activa ? (sort!.dir === "asc" ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <Th className="!p-0 align-middle" style={{ width: w }}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={`Ordenar por ${typeof children === "string" ? children : sortKey}`}
        className={`flex w-full items-center gap-1 px-2.5 py-2 ${align === "right" ? "justify-end" : "justify-start"}`}
      >
        <Text size="xs" fw={600} className="whitespace-nowrap uppercase leading-tight text-ink-700 dark:text-ink-200">
          {children}
        </Text>
        <Icono
          size={15}
          stroke={2}
          className={`shrink-0 ${activa ? "text-multiva-600" : "text-ink-600 dark:text-ink-300"}`}
        />
      </button>
    </Th>
  );
}

/**
 * Celda de texto truncada a una línea (llena el ancho de la columna, layout
 * fijo) + tooltip con el texto completo SOLO cuando de verdad está truncado
 * (detección por overflow real, vía `TruncatedTooltip` compartido).
 */
function CeldaTexto({ texto, fw }: { texto: string | null | undefined; fw?: 500 | 600 }) {
  const val = texto ?? "—";
  return (
    <TruncatedTooltip label={val} disabled={!texto}>
      <Text size="sm" truncate fw={fw}>
        {val}
      </Text>
    </TruncatedTooltip>
  );
}

function BandejaDesktopRow({
  it,
  seleccionada,
  onToggle,
}: {
  it: BandejaAutorizacionItem;
  seleccionada: boolean;
  onToggle: () => void;
}) {
  return (
    <Tr>
      <Td className="!px-2 align-middle">
        <Checkbox
          aria-label={`Seleccionar solicitud ${it.folio}`}
          checked={seleccionada}
          onCheckedChange={() => onToggle()}
        />
      </Td>
      <Td>
        <CeldaTexto texto={it.folio} fw={600} />
      </Td>
      <Td>
        <CeldaTexto texto={it.organizacionNombre} />
      </Td>
      <Td>
        <CeldaTexto texto={it.areaNombre} />
      </Td>
      <Td>
        <TruncatedTooltip
          label={`${etiquetaTipoSolicitud(it.tipo)} · ${etiquetaSubtipoSolicitud(it.subtipo)}`}
        >
          <Stack gap={0} className="min-w-0">
            <Text size="sm" truncate fw={600}>
              {etiquetaTipoSolicitud(it.tipo)}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {etiquetaSubtipoSolicitud(it.subtipo)}
            </Text>
          </Stack>
        </TruncatedTooltip>
      </Td>
      <Td>
        <CeldaTexto texto={it.proveedorRazonSocial} fw={600} />
      </Td>
      <Td>
        <Text size="sm">{it.proveedorNumero !== null ? it.proveedorNumero : "—"}</Text>
      </Td>
      <Td>
        <CeldaTexto texto={it.rfcEmisor} />
      </Td>
      <Td>
        <CeldaTexto texto={it.descripcionPrimerConcepto} />
      </Td>
      <Td align="right">
        <Text size="sm" fw={700} className="whitespace-nowrap">
          {it.totalCfdi !== null ? `$${formatearMonto(it.totalCfdi, null, { minimumFractionDigits: 2 })}` : "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm" fw={600}>{it.monedaCfdi ?? "—"}</Text>
      </Td>
      <Td>
        <Badge size="sm" color="azulProfundo">
          {it.nivelActual - 1}/{it.cantidadNiveles}
        </Badge>
      </Td>
      <Td>
        <Text size="sm" className="whitespace-nowrap">{formatFechaHora(it.fechaCreacion)}</Text>
      </Td>
      <Td align="center">
        <Link
          to={`/autorizacion/${it.id}` as never}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-multiva-100 px-3 text-[13px] font-semibold text-multiva-800 transition-colors hover:bg-multiva-200 dark:bg-multiva-900/50 dark:text-multiva-100 dark:hover:bg-multiva-900/70"
        >
          Resolver
          <IconChecks size={14} stroke={2.2} />
        </Link>
      </Td>
    </Tr>
  );
}
